import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/auth.service';
import { trainingCentersService } from '../../services/training-centers.service';
import { API_ENDPOINTS } from '../../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Building2, 
  Phone, 
  Mail, 
  MapPin, 
  Globe, 
  Facebook, 
  Linkedin,
  Edit,
  Save,
  X,
  Shield,
  MessageSquare,
  Trash2,
  AlertTriangle
} from 'lucide-react';

interface TrainingCenterProfile {
  id: number;
  name: string;
  phone_number: string;
  about?: string;
  address?: string;
  website?: string;
  facebook_url?: string;
  linkedin_url?: string;
  logo?: string;
  category_id?: string;
  country?: string;
  wilaya_id?: number;
  city?: string;
}

const TrainingCenterProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<TrainingCenterProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        
        // Fetch profile data
        const profileData = await authService.getTrainingCenterInfo();
        setProfile(profileData);
        
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      await trainingCentersService.deleteAccount();
      // Clear auth data and logout
      logout();
      // Navigate to home page
      navigate('/');
    } catch (error) {
      alert('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-purple-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-brand-primary via-brand-primary/90 to-brand-secondary/80 rounded-2xl p-8 text-white shadow-xl"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <h1 className="text-2xl sm:text-4xl font-bold">
                  {t('trainingCenter.profile.title')}
                </h1>
              </div>
              <p className="text-white/90 text-base sm:text-lg">
                {t('trainingCenter.profile.subtitle')}
              </p>
            </div>
            <div className="flex items-center gap-3 mt-6 lg:mt-0">
              <motion.button
                onClick={() => navigate('/training-center/edit-profile')}
                className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/30 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Edit className="h-5 w-5 mr-2" />
                {t('trainingCenter.profile.editButton')}
              </motion.button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="text-center">
                <div className="relative inline-block">
                  {profile?.logo ? (
                    <img
                      src={`${API_ENDPOINTS.UPLOADS}/${profile.logo}`}
                      alt="Logo"
                      className="h-32 w-32 rounded-full object-cover border-4 border-brand-primary/20 shadow-lg"
                      onError={(e) => {
                      }}
                    />
                  ) : (
                    <div className="h-32 w-32 rounded-full bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center border-4 border-brand-primary/20 shadow-lg">
                      <Building2 className="h-16 w-16 text-brand-primary" />
                    </div>
                  )}
                </div>
                
                <h2 className="mt-6 text-2xl font-bold text-gray-900">
                  {profile?.name || t('trainingCenter.profile.professionalCenter')}
                </h2>
                <p className="text-gray-600 mt-2">{t('trainingCenter.profile.professionalCenter')}</p>
                
                {/* Social Media Links */}
                <div className="mt-6 flex justify-center space-x-4">
                  {profile?.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-brand-primary hover:text-white transition-all duration-200 hover:scale-110"
                    >
                      <Globe className="h-5 w-5" />
                    </a>
                  )}
                  {profile?.facebook_url && (
                    <a
                      href={profile.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-blue-600 hover:text-white transition-all duration-200 hover:scale-110"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                  )}
                  {profile?.linkedin_url && (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-blue-700 hover:text-white transition-all duration-200 hover:scale-110"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  )}
                </div>

                {/* Status Badge */}
                <div className="mt-6">
                  <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 rounded-full text-sm font-medium">
                    <Shield className="h-4 w-4 mr-2" />
                    {t('trainingCenter.profile.verifiedBadge')}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Profile Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Basic Information */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="p-2 bg-brand-primary/20 rounded-lg">
                  <Building2 className="h-5 w-5 text-brand-primary" />
                </div>
                {t('trainingCenter.profile.professionalInfo')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.name')}
                  </label>
                  <p className="text-gray-900 font-medium">{profile?.name || t('trainingCenter.profile.fields.notSpecified')}</p>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.phone')}
                  </label>
                  <p className="text-gray-900 font-medium">{profile?.phone_number || t('trainingCenter.profile.fields.notSpecified')}</p>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.email')}
                  </label>
                  <p className="text-gray-900 font-medium">{user?.email || t('trainingCenter.profile.fields.notSpecified')}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('trainingCenter.profile.fields.emailNote')}</p>
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.website')}
                  </label>
                  <p className="text-gray-900 font-medium">{profile?.website || t('trainingCenter.profile.fields.notSpecified')}</p>
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.address')}
                  </label>
                  <p className="text-gray-900 font-medium">{profile?.address || t('trainingCenter.profile.fields.notSpecified')}</p>
                </div>

                {/* About */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.about')}
                  </label>
                  <p className="text-gray-900 leading-relaxed break-words whitespace-pre-wrap">{profile?.about || t('trainingCenter.profile.fields.notSpecified')}</p>
                </div>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="p-2 bg-brand-secondary/20 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-brand-secondary" />
                </div>
                {t('trainingCenter.profile.socialMedia')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.facebook')}
                  </label>
                  <p className="text-gray-900 font-medium">{profile?.facebook_url || t('trainingCenter.profile.fields.notSpecified')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('trainingCenter.profile.fields.linkedin')}
                  </label>
                  <p className="text-gray-900 font-medium">{profile?.linkedin_url || t('trainingCenter.profile.fields.notSpecified')}</p>
                </div>
              </div>
            </div>

            {/* Delete Account Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-red-200 p-8"
            >
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-900 mb-2">
                      {t('trainingCenter.profile.dangerZone.title')}
                    </h3>
                    <p className="text-sm text-red-700 mb-4">
                      {t('trainingCenter.profile.dangerZone.description')}
                    </p>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-all shadow-md flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                      {isDeleting ? t('trainingCenter.profile.deleting') : t('trainingCenter.profile.deleteButton')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {t('trainingCenter.profile.deleteModal.title')}
              </h3>
            </div>
            
            <p className="text-gray-700 mb-6">
              {t('trainingCenter.profile.deleteModal.description')}
            </p>
            
            <ul className="list-disc list-inside space-y-2 mb-6 text-sm text-gray-600">
              {(t('trainingCenter.profile.deleteModal.willDelete', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            
            <p className="text-sm font-semibold text-red-600 mb-6">
              {t('trainingCenter.profile.deleteModal.warning')}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                {t('trainingCenter.profile.deleteModal.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('trainingCenter.profile.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    {t('trainingCenter.profile.deleteModal.confirm')}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TrainingCenterProfilePage;

